<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="komornik-page">
    <div class="page-header">
      <div class="page-header-text">
        <div class="page-eyebrow">Finanse</div>
        <h1 class="page-title">Obsługa komornicza</h1>
      </div>
      <v-spacer></v-spacer>
      <v-btn v-if="tab === 1" color="primary" outlined class="mr-2" :loading="importing" @click="$refs.bailiffCsv.click()">
        <v-icon left>mdi-upload</v-icon>Importuj komorników z CSV
      </v-btn>
      <input ref="bailiffCsv" type="file" accept=".csv,text/csv" style="display:none" @change="onBailiffCsvPicked" />
      <v-btn v-if="tab === 0" color="primary" outlined @click="openTemplates">
        <v-icon left>mdi-file-document-edit</v-icon>Szablony odpowiedzi
      </v-btn>
    </div>

    <v-tabs v-model="tab" class="mb-4" background-color="transparent" @change="onTabChange">
      <v-tab>Pisma komornicze</v-tab>
      <v-tab>Komornicy</v-tab>
    </v-tabs>

    <!-- Dialog: edytor szablonów -->
    <v-dialog v-model="templatesDialog" max-width="820">
      <v-card>
        <v-card-title>Szablony odpowiedzi na pisma komornicze</v-card-title>
        <v-card-text>
          <div class="caption grey--text mb-3" v-pre>
            Placeholdery podstawiane automatycznie: {{kierowca}}, {{kierowca_pesel}}, {{kierowca_adres}},
            {{firma_nazwa}}, {{firma_nip}}, {{firma_adres}}, {{komornik_nazwa}},
            {{komornik_adres}}, {{sygnatura_akt}}, {{data}}, {{zbieg_lista}}.
          </div>
          <v-expansion-panels>
            <v-expansion-panel v-for="t in templates" :key="t.variant">
              <v-expansion-panel-header>{{ variantLabel(t.variant) }}</v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-text-field v-model="t.title" label="Tytuł szablonu" outlined dense />
                <v-textarea v-model="t.body" label="Treść" outlined rows="8" auto-grow />
                <v-btn small color="primary" :loading="templateSaving === t.variant" @click="saveTemplate(t)">Zapisz</v-btn>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="templatesDialog = false">Zamknij</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-tabs-items v-model="tab" style="background: transparent;" touchless>
    <v-tab-item>
    <v-card outlined class="rounded-lg mb-4">
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="search"
              label="Szukaj (kierowca lub tytuł)"
              outlined dense hide-details clearable
              prepend-inner-icon="mdi-magnify"
              @input="debouncedFetch"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="status"
              :items="statusOptions"
              label="Status"
              outlined dense hide-details
              @change="fetch"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="bailiffFilter"
              label="Komornik (nazwa)"
              outlined dense hide-details clearable
              prepend-inner-icon="mdi-gavel"
              @input="debouncedFetch"
            />
          </v-col>
          <v-col cols="6" md="2">
            <v-text-field
              v-model="dateFrom"
              label="Otrzymano od"
              type="date"
              outlined dense hide-details clearable
              @change="fetch"
            />
          </v-col>
          <v-col cols="6" md="2">
            <v-text-field
              v-model="dateTo"
              label="Otrzymano do"
              type="date"
              outlined dense hide-details clearable
              @change="fetch"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card outlined class="rounded-lg" style="overflow: hidden;">
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :server-items-length="count"
        :options.sync="options"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
        class="elevation-0"
        @update:options="fetch"
      >
        <template v-slot:item.driver="{ item }">
          <a
            v-if="item.driver"
            class="driver-link"
            title="Przejdź do konta kierowcy"
            @click.prevent="goToDriver(item.driver.id)"
          >{{ item.driver.firstName + ' ' + item.driver.lastName }}</a>
          <span v-else>—</span>
        </template>
        <template v-slot:item.receivedAt="{ item }">{{ formatDate(item.receivedAt) }}</template>
        <template v-slot:item.responseDeadline="{ item }">
          <span v-if="!item.responseDeadline">—</span>
          <span v-else :class="isOverdue(item) ? 'red--text font-weight-bold' : ''">{{ formatDate(item.responseDeadline) }}</span>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip x-small :color="statusColor(item)" dark>{{ statusLabel(item) }}</v-chip>
        </template>
        <template v-slot:item.feeCents="{ item }">{{ (item.feeCents / 100).toFixed(2) }} zł</template>
        <template v-slot:item.files="{ item }">
          <v-btn
            v-for="f in item.files" :key="f.id"
            icon x-small class="mr-1" title="Otwórz plik"
            @click="openFile(f.id)"
          ><v-icon small>mdi-file-document</v-icon></v-btn>
          <span v-if="!item.files || item.files.length === 0">—</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            x-small color="primary" outlined class="mr-1"
            :disabled="!letterPreviewFileId(item)"
            @click="previewLetter(item)"
          ><v-icon x-small left>mdi-eye</v-icon>Podgląd</v-btn>
          <!-- Wysyłka odpowiedzi e-mailem do komornika + potwierdzenie wysłania -->
          <template v-if="item.responses && item.responses.length">
            <v-btn
              icon x-small class="mr-1"
              :title="item.responses[0].sentAt ? ('Wysłano: ' + item.responses[0].sentTo) : 'Wyślij odpowiedź e-mailem do komornika'"
              @click="sendResponseEmail(item, item.responses[0])"
            >
              <v-icon small :color="item.responses[0].sentAt ? 'success' : 'primary'">
                {{ item.responses[0].sentAt ? 'mdi-email-check' : 'mdi-email-arrow-right' }}
              </v-icon>
            </v-btn>
            <v-btn
              v-if="item.responses[0].confirmationFile"
              icon x-small class="mr-1"
              title="Pobierz potwierdzenie wysłania"
              @click="openFile(item.responses[0].confirmationFile.id)"
            >
              <v-icon small color="teal">mdi-file-check</v-icon>
            </v-btn>
          </template>
          <v-btn v-if="!item.respondedAt" x-small color="success" class="mr-1" @click="markResponded(item)">Odpowiedziano</v-btn>
          <v-btn v-else x-small text @click="reopen(item)">Cofnij</v-btn>
        </template>
      </v-data-table>
    </v-card>
    </v-tab-item>

    <!-- Zakładka: Komornicy -->
    <v-tab-item>
      <v-card outlined class="rounded-lg mb-4">
        <v-card-text>
          <v-text-field
            v-model="bailiffSearch"
            label="Szukaj komornika (nazwa, konto, kierowca)"
            outlined dense hide-details clearable
            prepend-inner-icon="mdi-magnify"
            @input="debouncedBailiffFetch"
          />
        </v-card-text>
      </v-card>
      <v-card outlined class="rounded-lg" style="overflow: hidden;">
        <v-data-table
          :headers="bailiffHeaders"
          :items="bailiffs"
          :loading="bailiffsLoading"
          :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
          class="elevation-0"
          no-data-text="Brak komorników"
        >
          <template v-slot:item.driver="{ item }">
            <a v-if="item.driver" class="driver-link" title="Przejdź do konta kierowcy" @click.prevent="goToDriver(item.driver.id)">{{ item.driver.firstName + ' ' + item.driver.lastName }}</a>
            <span v-else>—</span>
          </template>
          <template v-slot:item.isActive="{ item }">
            <v-chip x-small :color="item.isActive ? 'green' : (item.closedAt ? 'blue-grey' : 'grey')" dark>
              {{ item.isActive ? 'Aktywne' : (item.closedAt ? 'Spłacone' : 'Nieaktywne') }}
            </v-chip>
          </template>
          <template v-slot:item.transferTypes="{ item }">
            <v-chip x-small class="mr-1" v-for="t in item.transferTypes" :key="t">{{ transferTypeLabels[t] || t }}</v-chip>
          </template>
          <template v-slot:item.progress="{ item }">
            <span v-if="item.totalAmountCents != null">{{ ((item.paidAmountCents || 0) / 100).toFixed(2) }} / {{ (item.totalAmountCents / 100).toFixed(2) }} zł</span>
            <span v-else class="grey--text">{{ ((item.paidAmountCents || 0) / 100).toFixed(2) }} zł</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn x-small color="primary" @click="openBailiffEdit(item)"><v-icon x-small left>mdi-pencil</v-icon>Edytuj</v-btn>
          </template>
        </v-data-table>
      </v-card>
    </v-tab-item>
    </v-tabs-items>

    <!-- Dialog edycji komornika -->
    <v-dialog v-model="bailiffDialog" max-width="600">
      <v-card>
        <v-card-title>Edytuj komornika</v-card-title>
        <v-card-text>
          <div class="caption grey--text mb-2" v-if="bailiffForm.driverName">Kierowca: {{ bailiffForm.driverName }}</div>
          <v-text-field v-model="bailiffForm.name" label="Nazwa komornika" outlined dense />
          <v-text-field v-model="bailiffForm.accountNo" label="Numer konta" outlined dense />
          <v-text-field v-model="bailiffForm.wireTitle" label="Tytuł przelewu" outlined dense />
          <v-text-field v-model="bailiffForm.addressLine1" label="Adres 1" outlined dense />
          <v-text-field v-model="bailiffForm.addressLine2" label="Adres 2" outlined dense />
          <v-text-field v-model="bailiffForm.email" label="E-mail komornika" outlined dense />
          <v-select v-model="bailiffForm.transferTypes" :items="transferTypeOptions" label="Przejmuje" multiple chips small-chips outlined dense />
          <v-row dense>
            <v-col cols="6"><v-text-field v-model.number="bailiffForm.totalAmountZl" label="Kwota zajęcia (zł)" type="number" step="0.01" min="0" outlined dense /></v-col>
            <v-col cols="6"><v-text-field v-model.number="bailiffForm.paidAmountZl" label="Spłacono (zł)" type="number" step="0.01" min="0" outlined dense /></v-col>
          </v-row>
          <v-checkbox v-model="bailiffForm.isActive" label="Aktywne" />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="bailiffDialog = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="bailiffSaving" @click="saveBailiffEdit">Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import * as dayjs from "dayjs";

export default {
  name: "KomornikLettersPage",
  data: () => ({
    tab: 0,
    loading: false,
    importing: false,
    items: [],
    count: 0,
    search: "",
    status: "",
    bailiffFilter: "",
    dateFrom: "",
    dateTo: "",
    options: {},
    debounceTimer: null,
    // Zakładka "Komornicy"
    bailiffs: [],
    bailiffsLoading: false,
    bailiffSearch: "",
    bailiffDebounce: null,
    bailiffsFetched: false,
    bailiffDialog: false,
    bailiffSaving: false,
    editingBailiffId: null,
    bailiffForm: {
      name: "", accountNo: "", wireTitle: "", addressLine1: "", addressLine2: "",
      email: "", transferTypes: [], isActive: false, totalAmountZl: null,
      paidAmountZl: null, driverName: "",
    },
    bailiffHeaders: [
      { text: "Status", value: "isActive", sortable: false, width: "100px" },
      { text: "Kierowca", value: "driver" },
      { text: "Komornik", value: "name" },
      { text: "Nr konta", value: "accountNo" },
      { text: "Tytuł przelewu", value: "wireTitle" },
      { text: "Przejmuje", value: "transferTypes", sortable: false },
      { text: "Spłata", value: "progress", sortable: false },
      { text: "Akcje", value: "actions", sortable: false },
    ],
    transferTypeOptions: [
      { text: "Zleceniówka", value: "ZLECENIOWKA" },
      { text: "Zwrot kosztów", value: "ZWROT_KOSZTOW" },
      { text: "Najem", value: "NAJEM" },
    ],
    transferTypeLabels: { ZLECENIOWKA: "Zleceniówka", ZWROT_KOSZTOW: "Zwrot kosztów", NAJEM: "Najem" },
    templatesDialog: false,
    templates: [],
    templateSaving: null,
    variantLabels: {
      WORKS: "Pracuje / współpracuje",
      NOT_WORKS: "Nie pracuje / nie współpracuje",
      GARNISHMENT: "Zbieg egzekucji (zbieg zajęć)",
      CUSTOM: "Własne pismo",
    },
    statusOptions: [
      { text: "Wszystkie", value: "" },
      { text: "Oczekuje", value: "PENDING" },
      { text: "Po terminie", value: "OVERDUE" },
      { text: "Odpowiedziano", value: "RESPONDED" },
    ],
    headers: [
      { text: "Kierowca", value: "driver" },
      { text: "Tytuł / sygnatura", value: "title" },
      { text: "Otrzymano", value: "receivedAt" },
      { text: "Termin odpowiedzi", value: "responseDeadline" },
      { text: "Status", value: "status", sortable: false },
      { text: "Opłata", value: "feeCents", sortable: false },
      { text: "Pliki", value: "files", sortable: false },
      { text: "Akcje", value: "actions", sortable: false },
    ],
  }),
  async created() {
    await this.fetch();
  },
  methods: {
    formatDate(d) {
      return d ? dayjs(d).format("YYYY-MM-DD") : "—";
    },
    goToDriver(id) {
      if (id) window.location.href = "/driver-details?id=" + id;
    },
    async onBailiffCsvPicked(e) {
      const file = e.target.files && e.target.files[0];
      if (this.$refs.bailiffCsv) this.$refs.bailiffCsv.value = "";
      if (!file) return;
      this.importing = true;
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "bailiffs/import-csv",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
          data: fd,
        });
        const d = (res.data && res.data.data) || {};
        const unmatched = (d.unmatched || []).slice(0, 30).join("<br>");
        const conflicts = (d.accountConflicts || []).slice(0, 30).join("<br>");
        const invalids = (d.accountInvalids || []).slice(0, 30).join("<br>");
        Swal.fire({
          icon: "success",
          title: "Import zakończony",
          html:
            `<div style="text-align:left">` +
            `<b>Numery kont kierowców</b><br>` +
            `Ustawiono pod profilem: <b>${d.accountsSet ?? 0}</b><br>` +
            `Już takie samo: <b>${d.accountsSame ?? 0}</b><br>` +
            `Konflikt (profil ma inne — nie nadpisano): <b>${d.accountsConflict ?? 0}</b><br>` +
            `Błędny numer (≠26 cyfr — pominięto): <b>${d.accountsInvalid ?? 0}</b><br><br>` +
            `<b>Zajęcia komornicze</b><br>` +
            `Zajęć w pliku: <b>${d.garnishments ?? 0}</b><br>` +
            `Utworzono: <b>${d.created ?? 0}</b><br>` +
            `Już istniało (pominięto): <b>${d.exists ?? 0}</b><br>` +
            `Bez numeru konta: <b>${d.noAccount ?? 0}</b><br>` +
            `Bez dopasowanego kierowcy: <b>${d.noDriver ?? 0}</b>` +
            (d.multi && d.multi.length ? `<br><br>Wiersze z kilkoma komornikami (do ręcznej weryfikacji): <b>${d.multi.length}</b>` : "") +
            (conflicts ? `<br><br><span style="color:#b33">Konflikt numeru konta (sprawdź ręcznie):</span><br><small>${conflicts}</small>` : "") +
            (invalids ? `<br><br><span style="color:#b33">Błędny numer konta (≠26 cyfr — popraw w CSV i wgraj ponownie):</span><br><small>${invalids}</small>` : "") +
            (unmatched ? `<br><br><span style="color:#b33">Niedopasowani (sprawdź e-mail):</span><br><small>${unmatched}</small>` : "") +
            `</div>`,
          width: 600,
        });
        await this.fetch();
      } catch (err) {
        console.log(err);
        Swal.fire("Błąd", err.response?.data?.message?.toString() || "Nie udało się zaimportować pliku", "error");
      } finally {
        this.importing = false;
      }
    },
    // Najpierw podgląd wygenerowanej odpowiedzi (jeśli jest), w przeciwnym razie
    // skan samego pisma komorniczego. Zwraca ID pliku (nie url — w bazie jest
    // tylko klucz S3, plik serwuje backend przez aws/download-file/:id).
    letterPreviewFileId(item) {
      const resp = (item.responses || []).find((r) => r.file && r.file.id);
      if (resp) return resp.file.id;
      const f = (item.files || []).find((x) => x.id);
      return f ? f.id : null;
    },
    async openFile(fileId) {
      if (!fileId) return;
      try {
        const res = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "aws/download-file/" + fileId,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          responseType: "blob",
        });
        const blob = new Blob([res.data], { type: res.headers["content-type"] });
        window.open(URL.createObjectURL(blob), "_blank");
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", "Nie udało się otworzyć pliku", "error");
      }
    },
    // Wysyłka odpowiedzi e-mailem do komornika. Po udanej wysyłce backend
    // generuje PDF „Potwierdzenie wysłania" (przycisk mdi-file-check).
    async sendResponseEmail(letter, response) {
      const prefill = (letter.bailiff && letter.bailiff.email) || response.sentTo || "";
      const { value: email } = await Swal.fire({
        title: "Wyślij odpowiedź e-mailem",
        input: "email",
        inputLabel: "Adres e-mail komornika",
        inputValue: prefill,
        showCancelButton: true,
        confirmButtonText: "Wyślij",
        cancelButtonText: "Anuluj",
        inputValidator: (v) => (!v ? "Podaj adres e-mail" : null),
      });
      if (!email) return;
      try {
        const res = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "komornik-letters/response/" + response.id + "/send",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: { email },
        });
        await this.fetch();
        if (res.data?.data?.mocked) {
          Swal.fire("Uwaga", "SMTP nie jest skonfigurowany — e-mail zapisany tylko w logach (tryb testowy).", "warning");
        } else {
          Swal.fire("Wysłano", "Odpowiedź wysłana na " + email + ". Potwierdzenie wysłania jest dostępne przy piśmie.", "success");
        }
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Nie udało się wysłać", "error");
      }
    },
    previewLetter(item) {
      this.openFile(this.letterPreviewFileId(item));
    },
    onTabChange(t) {
      // Lista komorników ładowana leniwie przy pierwszym wejściu w zakładkę.
      if (t === 1 && !this.bailiffsFetched) this.fetchBailiffs();
    },
    debouncedBailiffFetch() {
      clearTimeout(this.bailiffDebounce);
      this.bailiffDebounce = setTimeout(() => this.fetchBailiffs(), 400);
    },
    async fetchBailiffs() {
      this.bailiffsLoading = true;
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "bailiffs/all",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          params: { search: this.bailiffSearch || "" },
        });
        this.bailiffs = res.data.data || [];
        this.bailiffsFetched = true;
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Nie udało się pobrać komorników", "error");
      }
      this.bailiffsLoading = false;
    },
    openBailiffEdit(item) {
      this.editingBailiffId = item.id;
      this.bailiffForm = {
        name: item.name || "",
        accountNo: item.accountNo || "",
        wireTitle: item.wireTitle || "",
        addressLine1: item.addressLine1 || "",
        addressLine2: item.addressLine2 || "",
        email: item.email || "",
        transferTypes: item.transferTypes || [],
        isActive: !!item.isActive,
        totalAmountZl: item.totalAmountCents != null ? item.totalAmountCents / 100 : null,
        paidAmountZl: item.paidAmountCents != null ? item.paidAmountCents / 100 : 0,
        driverName: item.driver ? item.driver.firstName + " " + item.driver.lastName : "",
      };
      this.bailiffDialog = true;
    },
    async saveBailiffEdit() {
      this.bailiffSaving = true;
      try {
        const toCents = (v) => (v === null || v === "" || isNaN(v) ? null : Math.round(Number(v) * 100));
        await axios({
          method: "patch",
          url: process.env.VUE_APP_ROOT_API + "bailiffs/" + this.editingBailiffId,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: {
            name: this.bailiffForm.name,
            accountNo: this.bailiffForm.accountNo,
            wireTitle: this.bailiffForm.wireTitle,
            addressLine1: this.bailiffForm.addressLine1,
            addressLine2: this.bailiffForm.addressLine2,
            email: this.bailiffForm.email,
            transferTypes: this.bailiffForm.transferTypes,
            isActive: this.bailiffForm.isActive,
            totalAmountCents: toCents(this.bailiffForm.totalAmountZl),
            paidAmountCents: toCents(this.bailiffForm.paidAmountZl) ?? 0,
          },
        });
        this.bailiffDialog = false;
        await this.fetchBailiffs();
        Swal.fire("Sukces", "Zapisano komornika", "success");
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Nie udało się zapisać", "error");
      }
      this.bailiffSaving = false;
    },
    isOverdue(item) {
      return !item.respondedAt && item.responseDeadline && new Date(item.responseDeadline) < new Date();
    },
    statusLabel(item) {
      if (item.respondedAt) return "Odpowiedziano";
      if (this.isOverdue(item)) return "Po terminie";
      return "Oczekuje";
    },
    statusColor(item) {
      if (item.respondedAt) return "green";
      if (this.isOverdue(item)) return "red";
      return "orange";
    },
    debouncedFetch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.fetch(), 400);
    },
    async fetch() {
      this.loading = true;
      try {
        const page = this.options.page || 1;
        const take = this.options.itemsPerPage || 25;
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "komornik-letters",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          params: {
            skip: page,
            take,
            search: this.search || "",
            status: this.status || "",
            bailiff: this.bailiffFilter || "",
            dateFrom: this.dateFrom || "",
            dateTo: this.dateTo || "",
          },
        });
        this.items = response.data.data.result;
        this.count = response.data.data.count;
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Błąd ładowania", "error");
      }
      this.loading = false;
    },
    async markResponded(item) {
      await this.patch(item.id, { respondedAt: "now" });
    },
    async reopen(item) {
      await this.patch(item.id, { respondedAt: "null" });
    },
    variantLabel(v) {
      return this.variantLabels[v] || v;
    },
    async openTemplates() {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "komornik-letters/templates",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.templates = res.data.data;
        this.templatesDialog = true;
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", "Nie udało się pobrać szablonów", "error");
      }
    },
    async saveTemplate(t) {
      this.templateSaving = t.variant;
      try {
        await axios({
          method: "put",
          url: process.env.VUE_APP_ROOT_API + "komornik-letters/templates/" + t.variant,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: { title: t.title, body: t.body },
        });
        Swal.fire("Sukces", "Zapisano szablon", "success");
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
      this.templateSaving = null;
    },
    async patch(id, data) {
      try {
        await axios({
          method: "patch",
          url: process.env.VUE_APP_ROOT_API + "komornik-letters/" + id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data,
        });
        await this.fetch();
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
    },
  },
};
</script>

<style scoped>
.komornik-page { background: #181C28; min-height: calc(100vh - 56px); padding: 16px 16px 40px; margin: -24px; color: #fff; }
.komornik-page .driver-link { color: #5B8DEF; cursor: pointer; font-weight: 600; }
.komornik-page .driver-link:hover { text-decoration: underline; color: #88AAF5; }
.page-header { display: flex; align-items: center; gap: 12px; padding: 4px 4px 18px; }
.page-eyebrow { font-size: 12px; color: #6E7691; font-weight: 500; }
.page-title { font-size: 22px; font-weight: 800; color: #fff; margin: 2px 0 0; letter-spacing: -0.02em; }
.komornik-page >>> .v-card { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; box-shadow: none !important; color: #fff !important; }
.komornik-page >>> .v-data-table { background: transparent !important; color: #fff !important; }
.komornik-page >>> .v-data-table thead tr th { background: #1F2434 !important; color: #6E7691 !important; font-size: 11px !important; font-weight: 700 !important; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid rgba(255,255,255,0.06) !important; }
.komornik-page >>> .v-data-table tbody tr { background: transparent !important; }
.komornik-page >>> .v-data-table tbody tr:hover { background: #2A3042 !important; }
.komornik-page >>> .v-data-table tbody tr td { color: #fff !important; border-bottom: 1px solid rgba(255,255,255,0.04) !important; }
.komornik-page >>> .v-data-footer { background: #1F2434 !important; color: #B0BEC5 !important; border-top: 1px solid rgba(255,255,255,0.06) !important; }
</style>
